import { BetterBuyOptionProd, ClientTargetMappings } from "@/interfaces/enums";
import { ParsedUrlQuery } from "querystring";
import clientDeepLinks from "./clientDeepLinks";
import getDataSource from "@/interfaces/fortuneDataSource";
import { i18n } from "next-i18next";
import { UserTypeEnum } from "@/interfaces/copyTradingTypes";
export interface ShortLinkTransferResult {
	title: string;
	description: string;
	imageUrl: string;
	deepLink: string;
	symbol?: string;
	redirectUrl?: string;
	nickname?: string;
	campaign?: string; // 邀请码
	mediaSource?: string; // 场景
	channel?: string;
}
/**
 * 客户端生成的短链接转为带有meta share 并且具有deeplink的页面
 * @param {ParsedUrlQuery} parsedUrlQuery {t: 0, s: AAPL, id: 12312312 }
 * @param {String} locale 多语言标识
 * @return {Object} deepLink Meta{title, description, imageUrl}
 */
export default function shortLinkTransfer(parsedUrlQuery: ParsedUrlQuery, locale: string): ShortLinkTransferResult {
	try {
		const { t, s, id, m } = parsedUrlQuery;
		if (t === undefined) throw Error("missing Target");

		const _target = Number(t);

		switch (_target) {
			case ClientTargetMappings.stockDetail: {
				if (s === undefined) throw Error("stockDetail missing symbol");
				const _s = String(s);
				return {
					deepLink: clientDeepLinks.stockDetail(symbolToMarket(_s), _s, getExtraQuery(parsedUrlQuery, ["t", "s"])),
					symbol: _s,
					...getMeta(_target, locale),
				};
			}

			case ClientTargetMappings.option_detail: {
				if (s === undefined) throw Error("stockDetail missing symbol");
				const _s = String(s);
				return {
					deepLink: clientDeepLinks.optionDetail(_s, getExtraQuery(parsedUrlQuery, ["t", "s"])),
					symbol: _s,
					...getMeta(_target, locale),
				};
			}

			case ClientTargetMappings.option_betterBuy: {
				if (s === undefined) throw Error("option_betterBuy missing symbol");
				const _s = String(s);
				return {
					deepLink: clientDeepLinks.betterBuyOmnibus(undefined, _s, getExtraQuery(parsedUrlQuery, ["t", "s"])),
					symbol: _s,
					...getMeta(_target, locale),
				};
			}

			case ClientTargetMappings.betterBuyOmnibus: {
				if (s === undefined) throw Error("betterBuyOmnibus missing tab");
				const _s = String(s);
				return {
					deepLink: clientDeepLinks.betterBuyOmnibus(_s, undefined, getExtraQuery(parsedUrlQuery, ["t", "s"])),
					symbol: _s,
					...getMeta(_target, locale),
				};
			}

			case ClientTargetMappings.userHomePage: {
				if (id === undefined) throw Error("userHomePage missing userId");
				const _userId = String(id);
				const userType = m ? String(m) : undefined;
				const redirectUrl = userType === String(UserTypeEnum.AI_COMBO) ? `/${locale}/ai/portfolio/${_userId}` : "";

				return {
					deepLink: clientDeepLinks.userHonePage(_userId, getExtraQuery(parsedUrlQuery, ["t", "id"])),
					nickname: s ? String(s) : "",
					redirectUrl,
					...getMeta(_target, locale, s ? String(s) : "", m ? String(m) : undefined),
				};
			}
			case ClientTargetMappings.aiPortfolio: {
				return {
					deepLink: clientDeepLinks.aiPortfolio(),
					nickname: s ? String(s) : "",
					redirectUrl: `/${locale}/ai/portfolio`,
					...getMeta(_target, locale, s ? String(s) : "", m ? String(m) : undefined),
				};
			}

			case ClientTargetMappings.watchListDetail: {
				if (id === undefined) throw Error("watchListDetail missing watchlistId");
				const _whatListId = String(id);
				return {
					redirectUrl: `/${locale}/investing/watchlist/${_whatListId}`,
					deepLink: clientDeepLinks.watchListDetail(_whatListId, getExtraQuery(parsedUrlQuery, ["t", "id"])),
					...getMeta(_target, locale),
				};
			}

			case ClientTargetMappings.rockStarRankList: {
				if (s === undefined) throw Error("missing tab");

				return {
					deepLink: clientDeepLinks.rockStarRankList(String(s), getExtraQuery(parsedUrlQuery, ["t", "s"])),
					...getMeta(_target, locale, String(s)),
				};
			}
			case ClientTargetMappings.stock_better_buy: {
				if (s === undefined) throw Error("stock_better_buy missing symbol");
				const _s = String(s);
				return {
					deepLink: clientDeepLinks.stockButterBuy(_s, symbolToMarket(_s), getExtraQuery(parsedUrlQuery, ["t", "s"])),
					symbol: _s,
					...getMeta(_target, locale),
				};
			}
			case ClientTargetMappings.tradeGPTDetail: {
				if (s === undefined) throw Error("feedID missing");
				const _s = String(s);
				return {
					deepLink: clientDeepLinks.tradeGPTDetail(_s, getExtraQuery(parsedUrlQuery, ["t", "s"])),
					...getMeta(_target, locale),
					redirectUrl: `/${locale}/investing/feed/${_s}`,
				};
			}
			case ClientTargetMappings.tradeGPTList: {
				return {
					deepLink: clientDeepLinks.tradeGPTList(getExtraQuery(parsedUrlQuery, ["t", "s"])),
					...getMeta(_target, locale),
					redirectUrl: `/${locale}/investing/feed`,
				};
			}
			case ClientTargetMappings.positionSmartGuard: {
				return {
					deepLink: clientDeepLinks.positionSmartGuard(String(s), String(m)),
					...getMeta(_target, locale),
				};
			}
			case ClientTargetMappings.watchListGroup: {
				return {
					deepLink: clientDeepLinks.watchListGroup(String(s), String(m)),
					...getMeta(_target, locale),
				};
			}
			case ClientTargetMappings.rockBot: {
				return {
					deepLink: clientDeepLinks.rockBot(),
					...getMeta(_target, locale),
				};
			}
			case ClientTargetMappings.dailyFortune: {
				return {
					deepLink: "",
					redirectUrl: `/${locale}/daily-fortune?order=${String(s)}`,
					...getMeta(_target, locale, String(s)),
				};
			}
			case ClientTargetMappings.trump: {
				let redirectUrl = `/${locale}/activities/trump`;
				if (s) {
					if (id) {
						redirectUrl += `?s=${String(s)}&id=${String(id)}`;
					}
				}
				return {
					deepLink: "",
					symbol: String(id),
					redirectUrl,
					...getMeta(_target, locale, String(s)),
				};
			}
			case ClientTargetMappings.aiApp: {
				let redirectUrl = `/${locale}/activities/ai-app`;
				if (s) {
					if (id) {
						redirectUrl += `?s=${String(s)}&id=${String(id)}&m=${String(m)}`;
					}
				}
				return {
					deepLink: "",
					symbol: String(id),
					redirectUrl,
					...getMeta(_target, locale, String(s), String(m)),
				};
			}

			case ClientTargetMappings.cryptoBTW: {
				let redirectUrl = `/${locale}/activities/btw`;
				if (s) {
					if (id) {
						redirectUrl += `?s=${String(s)}&id=${String(id)}&m=${String(m)}`;
					}
				}
				return {
					deepLink: "",
					symbol: String(id),
					redirectUrl,
					...getMeta(_target, locale, String(s), String(m)),
				};
			}
			case ClientTargetMappings.flyingStock: {
				let redirectUrl = `/${locale}/activities/flying`;
				if (s) {
					if (id) {
						redirectUrl += `?s=${String(s)}&id=${String(id)}`;
					}
				}
				return {
					deepLink: "",
					symbol: String(id),
					redirectUrl,
					...getMeta(_target, locale, String(s)),
				};
			}
			default:
				return {
					deepLink: clientDeepLinks.assetPage(getExtraQuery(parsedUrlQuery, ["t"])),
					title: "",
					description: "",
					imageUrl: "",
					symbol: "",
				};
		}
	} catch (error) {
		throw error;
	}
}

/**
 * @summary 获取oneLink参数
 */
export function getOneLinkParams(parsedUrlQuery: ParsedUrlQuery) {
	const { c, e, p } = parsedUrlQuery;
	return {
		campaign: c ? String(c) : "",
		mediaSource: e ? getShareSceneKey(String(e)) : "",
		channel: p ? getShareScenePlatformKey(String(p)) : "",
	};
}

/**
 * 通过Symbol 得到市场
 * @param {String} symbol
 * @return {String}
 */
export function symbolToMarket(symbol: string) {
	const commonMarketSuffix = symbol.substring(symbol.lastIndexOf("|") + 1);
	if (commonMarketSuffix.length == symbol.length) {
		const withDotStockMarketSuffix = symbol.substring(symbol.lastIndexOf(".") + 1);
		return withDotStockMarketSuffix.length != symbol.length && withDotStockMarketSuffix === "HK" ? "HK" : "US";
	} else {
		return commonMarketSuffix.startsWith("OS") ? commonMarketSuffix.substring(0, 4) : commonMarketSuffix;
	}
}

/**
 * 获取额外的参数
 * @param  {ParsedUrlQuery}
 * @param {Array[string]} ExtraKeys
 * @return {String}
 */
function getExtraQuery(parsedUrlQuery: ParsedUrlQuery, ExtraKeys: String[]) {
	let _query = "";
	for (let key in parsedUrlQuery) {
		if (ExtraKeys.includes(key)) {
			continue;
		}

		_query += `&${key}=${parsedUrlQuery[key]}`;
	}
	return _query;
}

/**
 * 通过枚举映射 Meta数据
 * @param {ClientTargetMappings}
 * @param {String} locale 多语言标识
 * @return {Object}
 *  title 【share.json 多语言key】
 *  description
 *  imageUrl
 */
function getMeta(type: ClientTargetMappings, locale: string, extra?: string, extra2?: string) {
	let title = "";
	// 通用邀请的description CR22-description
	let description = "CR22-description";
	const CND = process.env.NEXT_PUBLIC_APP_OSS_PUBLIC_CND;
	let imageUrl = `${CND}/h5/share/investment-v1.jpeg`;

	switch (type) {
		case ClientTargetMappings.stockDetail:
			title = "stock-detail-title";
			break;
		case ClientTargetMappings.option_detail:
			title = "option-detail-title";
			break;
		case ClientTargetMappings.option_betterBuy:
			title = "option-better-buy-title";
			break;
		case ClientTargetMappings.userHomePage: {
			if (extra) {
				title = "Im-copy-trading-rockstar-title";
				description = "Im-copy-trading-rockstar-description";
				// ai portfolio 分享
				if (extra2 === "2") {
				}
				break;
			} else {
				title = "user-home-page-title";
				break;
			}
		}

		case ClientTargetMappings.watchListDetail:
			title = "whatlist-detail-title";
			break;
		case ClientTargetMappings.rockStarRankList:
			title = `rock_star_rank_list-${extra}`;
			break;
		case ClientTargetMappings.betterBuyOmnibus:
			title = "betterBuyOmnibus_title";
			break;
		case ClientTargetMappings.stock_better_buy:
			title = "option-better-buy-title";
			break;
		case ClientTargetMappings.tradeGPTList:
			title = "investing-trade-gpt-list-title";
			description = "investing-trade-gpt-list-description";
			imageUrl = `${CND}/h5/share/ai-strategy.png`;
			break;
		case ClientTargetMappings.tradeGPTDetail:
			title = "investing-trade-gpt-detail-title";
			description = "investing-trade-gpt-detail-description";
			break;
		case ClientTargetMappings.positionSmartGuard:
			title = "smart-guard-product-share-title";
			imageUrl = process.env.NEXT_PUBLIC_APP_OSS_PUBLIC_CND + "/h5/logo.png";
			break;
		case ClientTargetMappings.watchListGroup:
			title = "AR22-title";
		case ClientTargetMappings.rockBot:
			title = "AR22-title";
		case ClientTargetMappings.flyingStock: {
			imageUrl = `${CND}/h5/activities/flying/hero.png`;
			title = extra === "2" ? "i-have-voted-to-bullish" : "catch-the-next-flying-tesla-and-participate";
			description = extra === "2" ? "spend-5-minutes-with-rockflow-again" : "spend-5-minutes-with-rockflow";
			break;
		}
		case ClientTargetMappings.trump: {
			imageUrl = `${CND}/h5/activities/trump/hero.png`;
			title = extra === "2" ? "i-have-voted-to-bullish" : "which-will-be-next-bull-stock";
			description = extra === "2" ? "trump-concept-stocks-comeback" : "join-rockflow-to-predict";
			break;
		}
		case ClientTargetMappings.aiApp: {
			imageUrl = `${CND}/h5/activities/ai-app/hero.jpeg`;

			if (extra === "2") {
				title = `ai-app-share-title-${extra2 || 1}`;
				description = "ai-app-share-desc";
			} else {
				title = "ai-app-page-title";
				description = "ai-app-page-desc";
			}
			break;
		}
		case ClientTargetMappings.cryptoBTW: {
			imageUrl = `${CND}/h5/activities/btw/hero.jpeg`;

			if (extra === "2") {
				title = `btw-share-title`;
				description = "btw-share-desc";
			} else {
				title = "btw-page-title";
				description = "btw-page-desc";
			}
			break;
		}
		case ClientTargetMappings.dailyFortune:
			{
				try {
					const config = getDataSource(Number(extra) || 1);
					const isExistTitle = i18n?.exists(`daily-fortune:${config?.title}`);
					const isExistDes = i18n?.exists(`daily-fortune:${config?.description}`);
					if (isExistTitle) {
						title = `${i18n?.t(`daily-fortune:${config?.title}`)} | ${i18n?.t("daily-fortune:today-is-luck-card")}`;
					}
					if (isExistDes) {
						description = i18n?.t(`daily-fortune:${config?.description}`) || "";
					}
					imageUrl = `${process.env.NEXT_PUBLIC_APP_OSS_PUBLIC_CND}/h5${config?.img}`;
				} catch (error) {
					console.log(error);
				}
			}
			break;
		default:
			break;
	}

	return {
		title,
		description,
		imageUrl,
	};
}

/**
 * 期权是小牛还是小熊 
 * 包含 |OS 并且以 L结尾  -- 小熊小牛
 * 中间字符
 *  C --- 小牛
 *  P --- 小熊
 *  eg: AAPL  230818C00250000|OSUSL 小牛
        AAPL  230915P00300000|OSUSL 小熊

 * return
 * 0 非小牛小熊
 * 1 小牛
 * 2 小熊
 */
export function symbolToBearOrBull(symbol: string) {
	const isBetterBuyProduct = symbol.includes("|OS") && symbol.substr(-1) === "L";
	if (!isBetterBuyProduct) return BetterBuyOptionProd.unknown;
	// 从空格开始 截取中间字段判断
	const space = symbol.indexOf(" ") || 0;
	const suffix = symbol.indexOf("|");
	if (suffix === -1) return BetterBuyOptionProd.unknown;

	const str = symbol.substring(space, suffix);
	if (str.includes("C")) return BetterBuyOptionProd.bull;
	if (str.includes("P")) return BetterBuyOptionProd.bear;
	return BetterBuyOptionProd.unknown;
}

/**
 * @summary 分享场景
 */
export enum ShareScene {
	homeAssetsShare = 1, // 首页资产分享
	betterBuyHomePageDynamic, // 好买-个人主页动态
	betterBuyCopyTradingDynamic, // 好买-跟单动态
	betterBuyAssets, // 好买-资产页面
	betterBuyDetail, // 好买-详情
	betterBuyOrderDetail, // 好买-订单详情
	closePositionOrderSuccess, // 平仓-交易成功
	closePositionOrderList, // 平仓-订单列表
	closePositionOrderDetail, // 平仓-订单详情
	positionShareOptionDetail, // 持仓分享-期权详情
	positionShareStockDetail, // 持仓分享-股票详情
	positionShareAssets, // 持仓分享-资产页面
	profitShareSupportShareGuide, // 盈利分享-现已支持持仓分享引导弹框
	profitShareAssets, // 盈利分享-资产页
	profitShareTodayProfit, // 盈利分享-今日盈利分享
	tradeShareCopyTradingDynamic, // 交易分享-跟单动态
	tradeShareHomepageDynamic, // 交易分享-个人主页动态
	tradeGptLists, // tradeGpt-列表
	tradeGptDetailRightTop, // tradeGpt-详情-右上角
	tradeGptDetailShareBtn, // tradeGpt-详情-分享按钮
	quoteShareStockDetail, // 行情分享(未持有股票)-股票详情
	quoteShareOptionDetail, // 行情分享(未持有期权)-期权详情
	userShareHomePageQrcode, // 用户分享-个人主页-二维码
	userShareHomePageShareBtn, // 用户分享-个人主页-分享按钮
	userShareContinuouslyOnBoard, // 用户分享-连续上榜
	userShareCopyTradingPlShare, // 用户分享-跟单榜单-收益率分享
	watchListDetailShare, // 股单详情分享
	gptListsPageShare, // gpt列表页分享
	copyTradingRankListsShare, // 牛人榜单分享
	rockBotChatItemShare, // RockBot列表item分享
	earnCalendarShare, // 财报交易分享
	screenShotShare, // 截图分享
}

/**
 * @summary 分享场景 -- 平台
 */
export enum ShareScenePlatform {
	qrcode = 1, // 扫码
	copy_link, // 复制链接
	facebook_group, // Facebook 群组
	facebook_new_feed, // FaceBook动态消息
	messenger, // Messager
	instagram, // instagram
	line, // line
	more, // more
	snapchat, // snapchat
	telegram, // telegram
	twitter_direct_meessage, // X 私信
	tweet, // X 推文
	wechat_friends, // 朋友圈
	wechat, // 微信
	whatsApp, // whatsApp
	xiaohongshu, // 小红书
	click_share_img_link, // 文字 + 链接
}

/**
 * 根据数值获取 ShareScene 枚举的键名
 * @param {number} value 枚举值
 * @return {string} 枚举键名
 */
export function getShareSceneKey(value: number | string): string {
	const _value = Number(value);

	const shareSceneKeys = Object.keys(ShareScene).filter((key) => isNaN(Number(key)));
	const shareSceneValues = Object.values(ShareScene).filter((val) => typeof val === "number");
	const index = shareSceneValues.indexOf(_value);
	return index !== -1 ? shareSceneKeys[index] : "Unknown";
}

/**
 * 根据数值获取 ShareScenePlatform 枚举的键名
 * @param {number} value 枚举值
 * @return {string} 枚举键名
 */
export function getShareScenePlatformKey(value: number | string): string {
	const _value = Number(value);
	const keys = Object.keys(ShareScenePlatform).filter((key) => isNaN(Number(key)));
	const values = Object.values(ShareScenePlatform).filter((val) => typeof val === "number");
	const index = values.indexOf(_value);
	return index !== -1 ? keys[index] : "Unknown";
}

/**
 * @summary 存储来源
 * @description oneLink 使用
 *
 */
export function saveShareParams(mediaSource?: string, campaign?: string, channel?: string) {
	if (typeof window === "undefined") return;
	// 存储来源
	if (mediaSource) {
		localStorage.setItem("mediaSource", mediaSource);
	}
	if (campaign) {
		localStorage.setItem("campaign", campaign);
	}
	if (channel) {
		localStorage.setItem("channel", channel);
	}
}
